import { useState } from 'react';
import { Ruler, Search, AlertTriangle, X, ShieldCheck } from 'lucide-react';

const INK    = '#2B2620';
const WALNUT = '#8B4513';
const MOSS   = '#3B6B44';
const OCHRE  = '#A6752A';
const RUST   = '#A13D2D';
const STONE  = '#8C8275';
const FONT_DISPLAY = "'Fraunces', Georgia, serif";
const FONT_SANS    = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// ── Helpers ───────────────────────────────────────────────────────────────────
function dec(val) {
  if (val === null || val === undefined || val === '') return '—';
  const n = parseFloat(val);
  if (isNaN(n)) return '—';
  return n.toFixed(4).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

// Guard: if a value is accidentally an object (bad Firestore data), return fallback string.
// This prevents React error #31 "Objects are not valid as React children".
function safeStr(val, fallback = '') {
  if (val === null || val === undefined) return fallback;
  if (typeof val === 'object') return fallback; // never render an object as text
  return String(val);
}

// ── Warning detection — flags likely data problems before a job gets cut ──────
const CUT_SECTION_KEYS = ['frame', 'mullPosts', 'sash', 'jChannel', 'reinforcement', 'glazbead', 'misc'];

function getWindowWarnings(result) {
  const warnings = [];
  const overallW = result.overallW ?? result.width ?? null;
  const height   = result.height   ?? null;
  const qty      = result.qty ?? 1;

  if (!qty || qty <= 0) warnings.push('Quantity is 0');

  if (overallW == null || height == null) {
    warnings.push('Missing overall width/height');
  } else {
    if (overallW < 6 || overallW > 120) warnings.push(`Width looks unusual (${dec(overallW)}")`);
    if (height   < 6 || height   > 120) warnings.push(`Height looks unusual (${dec(height)}")`);
  }

  if (!result.glass || result.glass.length === 0) {
    warnings.push('No glass data');
  } else {
    result.glass.forEach((pane, i) => {
      if (pane.width == null || pane.height == null) {
        warnings.push(`Glass pane ${i + 1} missing dimensions`);
      }
    });
  }

  const hasCutRows = CUT_SECTION_KEYS.some(k => Array.isArray(result[k]) && result[k].length > 0);
  if (!hasCutRows) warnings.push('No frame/sash cut data');

  return warnings;
}

// ── Section color styles ──────────────────────────────────────────────────────
const SECTION_STYLES = {
  springBalance: { bg: '#e8f5e9', text: '#1b5e20', label: 'SPRING HEIGHT'  },
  glass:         { bg: '#e8f4ff', text: '#143c78', label: 'GLASS'          },
  frame:         { bg: '#dcf5e1', text: '#005a14', label: 'FRAME'          },
  sash:          { bg: '#fffacc', text: '#645000', label: 'SASH'           },
  reinforcement: { bg: '#ffe8d2', text: '#783200', label: 'REINFORCEMENT'  },
  mullPosts:     { bg: '#eee4ff', text: '#46008c', label: 'MULL POSTS'     },
  jChannel:      { bg: '#e0f7fa', text: '#006064', label: 'J-CHANNEL'      },
  glazbead:      { bg: '#ffdae4', text: '#780028', label: 'GLAZBEAD'       },
  misc:          { bg: '#eeeeee', text: '#323232', label: 'MISC'           },
  screen:        { bg: '#e3f2fd', text: '#0d47a1', label: 'SCREEN'         },
};

// ── Generic cut section ───────────────────────────────────────────────────────
function CutSection({ sectionKey, rows }) {
  if (!rows || rows.length === 0) return null;
  const sc = SECTION_STYLES[sectionKey] ?? SECTION_STYLES.misc;
  return (
    <div style={{ marginBottom: '10px' }}>
      <div style={{
        background: sc.bg, color: sc.text,
        fontSize: '10px', fontWeight: '700', padding: '4px 8px',
        letterSpacing: '0.06em',
        display: 'grid', gridTemplateColumns: '2.5fr 0.5fr 1fr',
        borderRadius: '4px 4px 0 0',
      }}>
        <span>{sc.label}</span>
        <span style={{ textAlign: 'center' }}>QTY</span>
        <span style={{ textAlign: 'right' }}>LENGTH</span>
      </div>
      {rows.map((row, i) => (
        <div key={i} style={{
          display: 'grid', gridTemplateColumns: '2.5fr 0.5fr 1fr',
          background: i % 2 === 0 ? '#ffffff' : '#fafafa',
          borderBottom: '1px solid #f0f0f0',
          padding: '3px 8px', fontSize: '10px', color: '#2b2b2b',
        }}>
          <span>{row.part ? `${safeStr(row.label)}  (${safeStr(row.part)})` : (safeStr(row.label) || '—')}</span>
          <span style={{ textAlign: 'center', fontWeight: '700' }}>{row.qty ?? '—'}</span>
          <span style={{ textAlign: 'right', fontFamily: 'monospace' }}>
            {row.length != null ? `${dec(row.length)}"` : '—'}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Glass section — shows grid sub-row per pane when grids selected ───────────
function GlassSection({ rows }) {
  if (!rows || rows.length === 0) return null;
  const sc = SECTION_STYLES.glass;
  return (
    <div style={{ marginBottom: '10px' }}>
      <div style={{
        background: sc.bg, color: sc.text,
        fontSize: '10px', fontWeight: '700', padding: '4px 8px',
        letterSpacing: '0.06em',
        display: 'grid', gridTemplateColumns: '2fr 0.5fr 1fr 1fr 1.5fr',
        borderRadius: '4px 4px 0 0',
      }}>
        <span>GLASS</span>
        <span style={{ textAlign: 'center' }}>QTY</span>
        <span style={{ textAlign: 'right' }}>WIDTH</span>
        <span style={{ textAlign: 'right' }}>HEIGHT</span>
        <span style={{ textAlign: 'right' }}>TYPE</span>
      </div>
      {rows.map((pane, i) => {
        // ── safeStr guards: if glassType or glassConfig is an object (bad Firestore
        //    data from old approvals), render empty string instead of crashing.
        const glassType = `${safeStr(pane.glassType)} ${safeStr(pane.glassConfig)}`.trim() || '—';
        const showGrids  = pane.grids === true && (pane.gridQtyV > 0 || pane.gridQtyH > 0);
        return (
          <div key={i}>
            <div style={{
              display: 'grid', gridTemplateColumns: '2fr 0.5fr 1fr 1fr 1.5fr',
              background: i % 2 === 0 ? '#ffffff' : '#fafafa',
              borderBottom: showGrids ? 'none' : '1px solid #f0f0f0',
              padding: '3px 8px', fontSize: '10px', color: '#2b2b2b',
            }}>
              <span style={{ fontWeight: '600' }}>{safeStr(pane.label) || '—'}</span>
              <span style={{ textAlign: 'center', fontWeight: '700' }}>{pane.qty ?? '—'}</span>
              <span style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                {pane.width  != null ? `${dec(pane.width)}"` : '—'}
              </span>
              <span style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                {pane.height != null ? `${dec(pane.height)}"` : '—'}
              </span>
              <span style={{ textAlign: 'right' }}>{glassType}</span>
            </div>
            {showGrids && (
              <div style={{
                background: '#ddeeff', borderBottom: '1px solid #b8d4f0',
                padding: '3px 8px 3px 18px',
                fontSize: '9.5px', color: '#143c78', fontStyle: 'italic',
                display: 'flex', flexWrap: 'wrap', gap: '0 16px',
              }}>
                <span>
                  <strong>Grid:</strong>&nbsp;
                  {safeStr(pane.gridType)}&nbsp;—&nbsp;{safeStr(pane.gridPattern)}
                  &nbsp;&nbsp;V{pane.gridQtyV} × H{pane.gridQtyH}
                </span>
                {pane.gridLenH != null &&
                  <span><strong>Len H:</strong>&nbsp;{dec(pane.gridLenH)}"</span>}
                {pane.gridLenV != null &&
                  <span><strong>Len V:</strong>&nbsp;{dec(pane.gridLenV)}"</span>}
                {pane.horizQty > 0 &&
                  <span><strong>Horiz bars:</strong>&nbsp;{pane.horizQty}</span>}
                {pane.vertQty > 0 &&
                  <span><strong>Vert bars:</strong>&nbsp;{pane.vertQty}</span>}
                {pane.gridColor &&
                  <span><strong>Color:</strong>&nbsp;{safeStr(pane.gridColor)}</span>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── StdScreen section ─────────────────────────────────────────────────────────
function StdScreenSection({ stdScreen }) {
  if (!stdScreen) return null;
  const sc = SECTION_STYLES.screen;

  const rows = [];

  if (stdScreen.widthQty > 0 && stdScreen.width != null) {
    rows.push({
      label: 'Screen Widths',
      qty:   stdScreen.widthQty,
      length: stdScreen.width,
      part:  stdScreen.screenPart || '',
    });
  }
  if (stdScreen.withPullQty > 0 && stdScreen.height != null) {
    rows.push({
      label: 'Screen Heights w/ Pull',
      qty:   stdScreen.withPullQty,
      length: stdScreen.height,
      part:  stdScreen.screenPullPart || '',
    });
  }
  if (stdScreen.noPullQty > 0 && stdScreen.height != null) {
    rows.push({
      label: 'Screen Heights W/O Pull',
      qty:   stdScreen.noPullQty,
      length: stdScreen.height,
      part:  stdScreen.screenPart || '',
    });
  }
  if (rows.length === 0 && stdScreen.qty > 0) {
    if (stdScreen.width != null)
      rows.push({ label: 'Screen Widths',  qty: stdScreen.qty, length: stdScreen.width  });
    if (stdScreen.height != null)
      rows.push({ label: 'Screen Heights', qty: stdScreen.qty, length: stdScreen.height });
  }

  if (rows.length === 0) return null;

  return (
    <div style={{ marginBottom: '10px' }}>
      <div style={{
        background: sc.bg, color: sc.text,
        fontSize: '10px', fontWeight: '700', padding: '4px 8px',
        letterSpacing: '0.06em',
        display: 'grid', gridTemplateColumns: '2.5fr 0.5fr 1fr',
        borderRadius: '4px 4px 0 0',
      }}>
        <span>{sc.label}</span>
        <span style={{ textAlign: 'center' }}>QTY</span>
        <span style={{ textAlign: 'right' }}>LENGTH</span>
      </div>
      {rows.map((row, i) => (
        <div key={i} style={{
          display: 'grid', gridTemplateColumns: '2.5fr 0.5fr 1fr',
          background: i % 2 === 0 ? '#ffffff' : '#fafafa',
          borderBottom: '1px solid #f0f0f0',
          padding: '3px 8px', fontSize: '10px', color: '#2b2b2b',
        }}>
          <span>{row.part ? `${safeStr(row.label)}  (${safeStr(row.part)})` : (safeStr(row.label) || '—')}</span>
          <span style={{ textAlign: 'center', fontWeight: '700' }}>{row.qty ?? '—'}</span>
          <span style={{ textAlign: 'right', fontFamily: 'monospace' }}>
            {row.length != null ? `${dec(row.length)}"` : '—'}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Window Card ───────────────────────────────────────────────────────────────
function WindowCard({ result, warnings }) {
  const overallW = result.overallW ?? result.width  ?? null;
  const height   = result.height   ?? null;
  const type     = result.type     ?? '';

  const isDH    = type === 'DH';
  const isCSMT  = type === 'CSMT';
  const is3LHS  = type === '3LHS' || type === '3HS';
  const isBSMT  = type === 'BSMT';
  const isPW    = type === 'PW';
  const isHS    = type === 'HS';

  const sum = result.summary ?? {};

  const summaryH = sum.sashHeight ?? sum.glassH ?? null;
  const summaryW = sum.sashWidth  ?? sum.glassW ?? null;

  const is3LHSSplit = is3LHS && sum.keepSashW != null && sum.lockSashW != null;

  const g0        = result.glass?.[0] ?? {};
  // ── safeStr guards on glass fields — prevents crash if Firestore has bad data ──
  const glassDesc = `${safeStr(g0.glassType)} ${safeStr(g0.glassConfig)}`.trim() || '—';

  const showGrids = g0.grids === true && (g0.gridQtyV > 0 || g0.gridQtyH > 0);
  const gridsDesc = showGrids
    ? `${safeStr(g0.gridType)} — ${safeStr(g0.gridPattern)} V${g0.gridQtyV} × H${g0.gridQtyH}`.trim()
    : 'None';

  const bal = result.balance;
  const balRow = bal
    ? [bal.size != null
        ? `Size ${bal.size}  ·  ${bal.notch || '—'}`
        : `Notch: ${bal.notch || '—'}`,
       `Qty: ${bal.qty ?? '—'}`]
    : null;

  const flex = result.flexScreen;
  const std  = result.stdScreen;

  const infoRows = [
    ['Config',  safeStr(result.config) || 'Single',
     'Qty',     String(result.qty ?? 1)],
    ['Width',   overallW != null ? `${dec(overallW)}"` : '—',
     'Height',  height   != null ? `${dec(height)}"` : '—'],

    ...(isCSMT ? [
      ['Handing',   safeStr(result.handing)   || '—',
       'Fin Type',  safeStr(result.finType)   || '—'],
      ['Glass',     glassDesc,
       'Indiv W',   result.indivW   != null ? `${dec(result.indivW)}"` : '—'],
      ...(result.mullCenter
        ? [['Ctr of Mull', `${dec(result.mullCenter)}"`,
            'Notch',       safeStr(result.notch) || '—']]
        : [['Notch', safeStr(result.notch) || '—', '', '']]),
    ] : isBSMT ? [
      ['Fin Type', safeStr(result.finType) || '—',
       'Glass',    glassDesc],
    ] : is3LHS ? [
      ['Fin Type', safeStr(result.finType) || '—',
       'Glass',    glassDesc],
    ] : [
      ['Fin Type', safeStr(result.finType) || '—',
       'Glass',    glassDesc],
    ]),

    ['Gas Fill', safeStr(g0.gasFill) || '—',
     'Grids',    gridsDesc],

    ...(balRow
      ? [['Balance', balRow[0], '', balRow[1]]]
      : []),

    ...(flex ? [
      ['Screen W', flex.width  != null ? `${dec(flex.width)}"` : '—',
       'Screen H', flex.height != null ? `${dec(flex.height)}"` : '—'],
      ['Mesh',     safeStr(flex.meshType)   || '—',
       'Type',     safeStr(flex.screenType) || '—'],
    ] : std ? [
      ['Screen W', std.width  != null ? `${dec(std.width)}"` : '—',
       'Screen H', std.height != null ? `${dec(std.height)}"` : '—'],
      ['Screen',   'Std Scrn', '', ''],
    ] : [
      ['Screen', 'None', '', ''],
    ]),
  ];

  return (
    <div style={{
      background: 'white', borderRadius: '12px', overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(139,69,19,0.08), 0 1px 4px rgba(0,0,0,0.04)',
      border: '1px solid rgba(139,69,19,0.12)',
      display: 'flex', flexDirection: 'column', marginBottom: '16px',
    }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{
        background: isCSMT
          ? '#7a3800'
          : isBSMT
          ? '#1a4a2e'
          : is3LHS
          ? '#1a3a6b'
          : '#6b2e08',
        padding: '10px 14px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ color: 'white', fontWeight: '700', fontSize: '13px' }}>
            {safeStr(result.label) || '—'}  —  {type || '—'}  —  {safeStr(result.color) || '—'}
          </div>
          {isCSMT && result.handing && (
            <div style={{ color: 'rgba(255,255,255,0.82)', fontSize: '10.5px', marginTop: '3px', fontWeight: '600' }}>
              Handing: {safeStr(result.handing)}
              {result.mullCenter ? `  •  Ctr of Mull: ${dec(result.mullCenter)}"` : ''}
            </div>
          )}
          {is3LHS && (
            <div style={{ color: 'rgba(255,255,255,0.82)', fontSize: '10.5px', marginTop: '3px' }}>
              Keep W: {dec(sum.keepSashW)}"  •  Lock W: {dec(sum.lockSashW)}"  •  Keep H: {dec(sum.keepSashH ?? sum.sashHeight)}"  •  Lock H: {dec(sum.lockSashH ?? sum.sashHeight)}"
            </div>
          )}
        </div>
        <span style={{
          color: 'rgba(255,255,255,0.9)', fontSize: '11px',
          background: 'rgba(255,255,255,0.15)', padding: '3px 10px',
          borderRadius: '6px', fontWeight: '600',
        }}>
          Qty {result.qty ?? 1}
        </span>
      </div>

      {/* ── Info rows ──────────────────────────────────────────────────────── */}
      <div style={{ borderBottom: '2px solid #f0ebe4' }}>
        {infoRows.map(([l1, v1, l2, v2], i) => (
          <div key={i} style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr',
            background: i % 2 === 0 ? '#fdfcfb' : 'white',
            padding: '4px 12px', fontSize: '10.5px',
            borderBottom: '1px solid #f5f0eb',
          }}>
            <span style={{ color: '#8B4513', fontWeight: '700' }}>{l1}</span>
            <span style={{ color: '#2b2b2b' }}>{safeStr(v1) || v1}</span>
            <span style={{ color: '#8B4513', fontWeight: '700' }}>{l2}</span>
            <span style={{ color: '#2b2b2b', textAlign: 'right' }}>{safeStr(v2) || v2}</span>
          </div>
        ))}
      </div>

      {/* ── Data warnings — flagged before print/cut, not after ─────────────── */}
      {warnings && warnings.length > 0 && (
        <div style={{
          padding: '8px 12px',
          background: `${RUST}0f`,
          borderBottom: `1px solid ${RUST}33`,
          display: 'flex', flexDirection: 'column', gap: '3px',
        }}>
          {warnings.map((w, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10.5px', color: RUST, fontWeight: '600' }}>
              <AlertTriangle size={12} strokeWidth={2} style={{ flexShrink: 0 }} /> {w}
            </div>
          ))}
        </div>
      )}

      {/* ── Section tables ─────────────────────────────────────────────────── */}
      {result.options && (
        <div style={{
          margin: '0', padding: '5px 12px',
          background: 'rgba(139,69,19,0.05)',
          borderBottom: '1px solid rgba(139,69,19,0.1)',
          borderLeft: '3px solid #8B4513',
          fontSize: '10.5px', color: '#5a3010',
        }}>
          <span style={{ fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: '6px' }}>Notes:</span>
          {safeStr(result.options)}
        </div>
      )}
      <div style={{ padding: '10px 12px', flex: 1 }}>
        {isDH && <CutSection sectionKey="springBalance" rows={result.springBalance} />}
        <GlassSection rows={result.glass} />
        <CutSection sectionKey="frame" rows={result.frame} />
        <CutSection sectionKey="mullPosts" rows={result.mullPosts} />
        <CutSection sectionKey="sash" rows={result.sash} />
        <CutSection sectionKey="jChannel" rows={result.jChannel} />
        <CutSection sectionKey="reinforcement" rows={result.reinforcement} />
        <CutSection sectionKey="glazbead" rows={result.glazbead} />
        <CutSection sectionKey="misc" rows={result.misc} />
        <StdScreenSection stdScreen={result.stdScreen} />
      </div>

      {/* ── Summary bars ───────────────────────────────────────────────────── */}
      <div style={{ borderTop: '2px solid #f0ebe4', background: '#fdfcfb' }}>

        <div style={{
          padding: '6px 12px', display: 'flex', flexDirection: 'column', gap: '4px',
          borderBottom: (isCSMT && sum.sashWeldH != null) ? '1px solid #f0ebe4' : 'none',
        }}>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '9px', fontWeight: '800', color: '#fff', background: '#8B4513',
              padding: '2px 8px', borderRadius: '4px', letterSpacing: '0.05em', whiteSpace: 'nowrap', flexShrink: 0 }}>
              FRAME SIZE
            </span>
            <span style={{ fontSize: '10.5px', color: '#6b2e08', fontWeight: '700',
              background: '#f3e6d8', padding: '3px 8px', borderRadius: '4px', flex: 1 }}>
              1 × W {dec(overallW)}"  H {dec(height)}"
            </span>
          </div>

          {is3LHSSplit && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '9px', fontWeight: '800', color: '#fff', background: '#8B4513',
                  padding: '2px 8px', borderRadius: '4px', letterSpacing: '0.05em', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  L SASH SIZE
                </span>
                <span style={{ fontSize: '10.5px', color: '#1a3f7a', fontWeight: '700',
                  background: '#ddeeff', padding: '3px 8px', borderRadius: '4px', flex: 1 }}>
                  2 × W {dec(sum.lockSashW)}"  H {dec(sum.lockSashH ?? sum.sashHeight)}"
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '9px', fontWeight: '800', color: '#fff', background: '#8B4513',
                  padding: '2px 8px', borderRadius: '4px', letterSpacing: '0.05em', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  K SASH SIZE
                </span>
                <span style={{ fontSize: '10.5px', color: '#1a3f7a', fontWeight: '700',
                  background: '#c8daf5', padding: '3px 8px', borderRadius: '4px', flex: 1 }}>
                  1 × W {dec(sum.keepSashW)}"  H {dec(sum.keepSashH ?? sum.sashHeight)}"
                </span>
              </div>
            </>
          )}

          {summaryH !== null && !is3LHSSplit && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '9px', fontWeight: '800', color: '#fff', background: '#8B4513',
                padding: '2px 8px', borderRadius: '4px', letterSpacing: '0.05em', whiteSpace: 'nowrap', flexShrink: 0 }}>
                SASH SIZE
              </span>
              <span style={{ fontSize: '10.5px', color: '#6b2e08', fontWeight: '700',
                background: '#ede0d0', padding: '3px 8px', borderRadius: '4px', flex: 1 }}>
                W {dec(summaryW)}"  H {dec(summaryH)}"
              </span>
            </div>
          )}
        </div>

        {isCSMT && sum.sashWeldH != null && (
          <div style={{ padding: '4px 12px 6px', fontSize: '10.5px', color: '#6b2e08', fontWeight: '700', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            <span style={{ background: '#e5d0bb', padding: '3px 8px', borderRadius: '5px' }}>
              Sash Weld H: {dec(sum.sashWeldH)}"
            </span>
            <span style={{ background: '#e5d0bb', padding: '3px 8px', borderRadius: '5px' }}>
              Sash Weld W: {dec(sum.sashWeldW)}"
            </span>
            {sum.sashWeldW_C != null && (
              <>
                <span style={{ background: '#c8b5a0', padding: '3px 8px', borderRadius: '5px' }}>
                  Ctr Weld W: {dec(sum.sashWeldW_C)}"
                </span>
                <span style={{ background: '#c8b5a0', padding: '3px 8px', borderRadius: '5px' }}>
                  Ctr Weld H: {dec(sum.sashWeldH_C)}"
                </span>
              </>
            )}
          </div>
        )}

        {isDH && sum.springH != null && (
          <div style={{ padding: '4px 12px 6px', fontSize: '10.5px', color: '#1b5e20', fontWeight: '700' }}>
            <span style={{ background: '#e8f5e9', padding: '3px 8px', borderRadius: '5px' }}>
              Spring Height: {dec(sum.springH)}"
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Totals — job scope at a glance, computed once from calculatedWindows ──────
function computeTotals(enriched) {
  let totalGlassPanes = 0;
  let totalLinearIn   = 0;
  let flaggedCount    = 0;

  enriched.forEach(({ w, warnings }) => {
    (w.glass || []).forEach(p => { totalGlassPanes += (p.qty || 1); });
    CUT_SECTION_KEYS.forEach(k => {
      (w[k] || []).forEach(row => {
        if (row.length != null && row.qty != null) totalLinearIn += row.length * row.qty;
      });
    });
    if (warnings.length > 0) flaggedCount += 1;
  });

  return { totalGlassPanes, totalLinearFt: totalLinearIn / 12, flaggedCount };
}

function StatTile({ label, value, color }) {
  return (
    <div style={{ flex: 1, minWidth: '110px' }}>
      <div style={{ fontSize: '10px', fontWeight: '700', color: STONE, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' }}>
        {label}
      </div>
      <div style={{ fontSize: '19px', fontWeight: '700', color: color || INK, fontFamily: FONT_DISPLAY }}>
        {value}
      </div>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function CutListPreview({ job, calculatedWindows }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [onlyFlagged, setOnlyFlagged] = useState(false);

  if (!calculatedWindows || calculatedWindows.length === 0) return null;

  const enriched = calculatedWindows.map(w => ({ w, warnings: getWindowWarnings(w) }));
  const { totalGlassPanes, totalLinearFt, flaggedCount } = computeTotals(enriched);

  const q = searchQuery.trim().toLowerCase();
  const filtered = enriched.filter(({ w, warnings }) => {
    if (onlyFlagged && warnings.length === 0) return false;
    if (q) {
      const hay = `${safeStr(w.label)} ${safeStr(w.type)} ${safeStr(w.color)}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const isFiltering = !!q || onlyFlagged;

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', paddingBottom: '40px', fontFamily: FONT_SANS }}>

      {/* Header */}
      <div style={{
        background: 'white', borderRadius: '14px', padding: '18px 24px',
        marginBottom: '16px',
        boxShadow: '0 4px 20px rgba(139,69,19,0.07)',
        border: '1px solid rgba(139,69,19,0.1)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{
            width: '42px', height: '42px', borderRadius: '10px',
            background: WALNUT,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 10px rgba(139,69,19,0.25)',
          }}><Ruler size={20} strokeWidth={2} color="white" /></div>
          <div>
            <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '600', color: '#2b2b2b', fontFamily: FONT_DISPLAY }}>
              Cut List Preview
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '2px' }}>
              <span style={{ fontSize: '12px', color: '#999' }}>{job?.customerName || '—'}</span>
              <span style={{ width: '3px', height: '3px', borderRadius: '50%', background: STONE, flexShrink: 0 }} />
              <span style={{ fontSize: '12px', color: '#999' }}>WO# {job?.woNumber || '—'}</span>
              <span style={{ width: '3px', height: '3px', borderRadius: '50%', background: STONE, flexShrink: 0 }} />
              <span style={{ fontSize: '12px', color: '#999' }}>{job?.orderDate || '—'}</span>
              <span style={{ width: '3px', height: '3px', borderRadius: '50%', background: STONE, flexShrink: 0 }} />
              <span style={{ fontSize: '12px', color: '#999' }}>{calculatedWindows.length} window{calculatedWindows.length !== 1 ? 's' : ''}</span>
            </div>
            {job?.notes && (
              <p style={{ margin: '5px 0 0', fontSize: '11px', color: '#7a4a1a', background: 'rgba(139,69,19,0.07)', borderLeft: '3px solid #8B4513', padding: '4px 8px', borderRadius: '0 4px 4px 0', maxWidth: '520px' }}>
                <strong style={{ fontWeight: '700' }}>Notes:</strong> {job.notes}
              </p>
            )}
          </div>
        </div>
        <div style={{
          background: WALNUT,
          color: 'white', borderRadius: '10px', padding: '8px 18px',
          fontSize: '12px', fontWeight: '700',
          boxShadow: '0 2px 8px rgba(139,69,19,0.3)',
        }}>
          {calculatedWindows.length} Window{calculatedWindows.length !== 1 ? 's' : ''} Calculated
        </div>
      </div>

      {/* Quick totals */}
      <div style={{
        background: 'white', borderRadius: '14px', padding: '16px 24px',
        marginBottom: '16px',
        boxShadow: '0 4px 20px rgba(139,69,19,0.07)',
        border: '1px solid rgba(139,69,19,0.1)',
        display: 'flex', flexWrap: 'wrap', gap: '16px',
      }}>
        <StatTile label="Windows" value={calculatedWindows.length} />
        <StatTile label="Glass Panes" value={totalGlassPanes} color={OCHRE} />
        <StatTile label="Total Cut Length" value={`${totalLinearFt.toFixed(1)} ft`} color={MOSS} />
        <StatTile
          label="Needs Review"
          value={flaggedCount}
          color={flaggedCount > 0 ? RUST : MOSS}
        />
      </div>

      {/* Search + flagged filter */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
        marginBottom: '16px',
      }}>
        <div style={{
          flex: 1, minWidth: '220px', display: 'flex', alignItems: 'center', gap: '8px',
          background: 'white', borderRadius: '10px', padding: '9px 14px',
          border: '1px solid rgba(139,69,19,0.15)',
        }}>
          <Search size={15} strokeWidth={2} color={STONE} style={{ flexShrink: 0 }} />
          <input
            type="text"
            name="cutlist-preview-search"
            autoComplete="off"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by window label, type, or color…"
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: '13px', color: INK, fontFamily: FONT_SANS, background: 'transparent' }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{ display: 'flex', border: 'none', background: 'transparent', color: STONE, cursor: 'pointer', padding: 0 }}
            >
              <X size={14} strokeWidth={2} />
            </button>
          )}
        </div>

        {flaggedCount > 0 && (
          <button
            onClick={() => setOnlyFlagged(v => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '9px 14px', borderRadius: '10px',
              border: `1px solid ${onlyFlagged ? RUST : RUST + '4d'}`,
              background: onlyFlagged ? RUST : `${RUST}0f`,
              color: onlyFlagged ? 'white' : RUST,
              fontSize: '12.5px', fontWeight: '700', fontFamily: FONT_SANS,
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            <AlertTriangle size={13} strokeWidth={2} /> Needs Review ({flaggedCount})
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div style={{
          background: 'white', borderRadius: '14px', padding: '40px 24px',
          textAlign: 'center', border: '1px solid rgba(139,69,19,0.1)',
        }}>
          <ShieldCheck size={28} strokeWidth={1.5} color={STONE} style={{ marginBottom: '10px' }} />
          <div style={{ fontSize: '13px', color: STONE, fontWeight: '600' }}>
            No windows match {isFiltering ? 'your search/filter' : ''}.
          </div>
          {isFiltering && (
            <button
              onClick={() => { setSearchQuery(''); setOnlyFlagged(false); }}
              style={{ marginTop: '12px', padding: '7px 16px', borderRadius: '8px', border: `1px solid ${WALNUT}`, background: 'transparent', color: WALNUT, fontSize: '12.5px', fontWeight: '600', cursor: 'pointer', fontFamily: FONT_SANS }}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        filtered.map(({ w, warnings }, i) => (
          <WindowCard key={i} result={w} warnings={warnings} />
        ))
      )}
    </div>
  );
}